/** Quantity and cost pair, already scaled by the ledger. */
export type QuantityValuePair = {
  quantity: string
  value: string
}

/** One row from GET reports/inventory-movement-summary. Display these fields; do not recompute them. */
export type InventoryMovementSummaryRow = {
  item?: { uuid?: string; name?: string } | null
  location?: { id?: number; name?: string } | null
  opening?: QuantityValuePair | null
  stock_in?: QuantityValuePair | null
  sold?: {
    quantity?: string
    cost_value?: string
    selling_value?: string
  } | null
  transfer_in?: QuantityValuePair | null
  transfer_out?: QuantityValuePair | null
  stock_out?: QuantityValuePair | null
  closing?: QuantityValuePair | null
}

/** Flat amount keys shared by data rows and the report totals footer. */
export type MovementSummaryAmounts = {
  opening_qty: string
  opening_value: string
  stock_in_qty: string
  stock_in_value: string
  sold_qty: string
  sold_cost_value: string
  sold_selling_value: string
  transfer_in_qty: string
  transfer_in_value: string
  transfer_out_qty: string
  transfer_out_value: string
  stock_out_qty: string
  stock_out_value: string
  closing_qty: string
  closing_value: string
}

/** Flat row for the table. Keys match the API amounts one-for-one. */
export type MovementSummaryTableRow = MovementSummaryAmounts & {
  item_uuid: string
  item_name: string
  location_id: number
  location_name: string
}

/**
 * Report-wide totals from GET reports/inventory-movement-summary.
 * Same movement buckets as a row, without item/location identity.
 */
export type InventoryMovementSummaryTotals = Omit<InventoryMovementSummaryRow, 'item' | 'location'>

export type InventoryMovementSummaryParams = {
  start_date: string
  end_date: string
  /**
   * Omit for the active branch.
   * Empty string asks for every branch (`branch_uuid=`), which needs multi-branch access.
   */
  branch_uuid?: string | null
  location_id?: number | null
  item_uuid?: string | null
  page?: number
  per_page?: number
}

export type InventoryMovementSummaryResponse = {
  data: InventoryMovementSummaryRow[]
  meta?: {
    current_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  total?: number
  /** Report-wide totals for the current filters. Prefer these over summing the page. */
  totals?: InventoryMovementSummaryTotals | null
}

const amount = (value: string | undefined | null): string => (value == null || value === '' ? '0' : value)

/** Copy API movement buckets onto flat amount keys. Missing buckets fall back to zero. */
export function toMovementSummaryAmounts(
  buckets: InventoryMovementSummaryTotals | InventoryMovementSummaryRow | null | undefined,
): MovementSummaryAmounts {
  return {
    opening_qty: amount(buckets?.opening?.quantity),
    opening_value: amount(buckets?.opening?.value),
    stock_in_qty: amount(buckets?.stock_in?.quantity),
    stock_in_value: amount(buckets?.stock_in?.value),
    sold_qty: amount(buckets?.sold?.quantity),
    sold_cost_value: amount(buckets?.sold?.cost_value),
    sold_selling_value: amount(buckets?.sold?.selling_value),
    transfer_in_qty: amount(buckets?.transfer_in?.quantity),
    transfer_in_value: amount(buckets?.transfer_in?.value),
    transfer_out_qty: amount(buckets?.transfer_out?.quantity),
    transfer_out_value: amount(buckets?.transfer_out?.value),
    stock_out_qty: amount(buckets?.stock_out?.quantity),
    stock_out_value: amount(buckets?.stock_out?.value),
    closing_qty: amount(buckets?.closing?.quantity),
    closing_value: amount(buckets?.closing?.value),
  }
}

/** Copy API amounts onto table keys. Missing buckets fall back to zero so the table still renders. */
export function toMovementSummaryTableRow(row: InventoryMovementSummaryRow): MovementSummaryTableRow {
  return {
    item_uuid: row.item?.uuid ?? '',
    item_name: row.item?.name ?? '—',
    location_id: Number(row.location?.id) || 0,
    location_name: row.location?.name ?? '—',
    ...toMovementSummaryAmounts(row),
  }
}

/** Map report-wide totals onto the same flat keys as table amount columns. */
export function toMovementSummaryTotals(
  totals: InventoryMovementSummaryTotals | null | undefined,
): MovementSummaryAmounts {
  return toMovementSummaryAmounts(totals)
}

/** Omit branch_uuid so the API uses the active branch. */
export const BRANCH_FILTER_ACTIVE = 'active'
/** Empty branch_uuid asks for every branch the caller may see. */
export const BRANCH_FILTER_ALL = 'all'

/**
 * Branch and location stay on the signed-in scope unless the caller may switch them.
 * Switching requires multi-branch access. A locked location is always the active one.
 */
export function resolveMovementSummaryScope(input: {
  canSwitchBranch: boolean
  canSwitchLocation: boolean
  branchFilter: string
  locationId: number | null
  activeLocationId: number | null
}): { branch_uuid?: string; location_id: number | null } {
  let branch_uuid: string | undefined
  if (input.canSwitchBranch) {
    if (input.branchFilter === BRANCH_FILTER_ALL) branch_uuid = ''
    else if (input.branchFilter !== BRANCH_FILTER_ACTIVE && input.branchFilter) branch_uuid = input.branchFilter
  }

  return {
    branch_uuid,
    location_id: input.canSwitchLocation ? input.locationId : input.activeLocationId,
  }
}

/** Query string for GET reports/inventory-movement-summary. */
export function buildInventoryMovementSummaryQuery(params: InventoryMovementSummaryParams): string {
  const query = new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
  })

  if (params.branch_uuid === '') {
    query.set('branch_uuid', '')
  } else if (params.branch_uuid) {
    query.set('branch_uuid', params.branch_uuid)
  }

  if (params.location_id != null) {
    query.set('location_id', String(params.location_id))
  }

  if (params.item_uuid) {
    query.set('item_uuid', params.item_uuid)
  }

  if (params.page != null) {
    query.set('page', String(params.page))
  }

  if (params.per_page != null) {
    query.set('per_page', String(params.per_page))
  }

  return query.toString()
}

export function readSummaryPage(
  payload: InventoryMovementSummaryResponse | undefined,
  requestedPage: number,
  rowCount: number,
): { page: number; total: number } {
  return {
    page: payload?.current_page ?? payload?.meta?.current_page ?? requestedPage,
    total: payload?.total ?? payload?.meta?.total ?? rowCount,
  }
}
